import React, { useEffect, useState } from 'react';
import { Search, Send } from 'lucide-react';
import { adminService } from '../services/adminService';
import { toast } from 'react-toastify';
// Import Font Awesome Instagram icon
import { FaInstagram } from 'react-icons/fa';

const UserInstagramDetails = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [instaFilter, setInstaFilter] = useState('all');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Bulk notification modal state
  const [showBulkNotifyModal, setShowBulkNotifyModal] = useState(false);
  const [bulkNotifyForm, setBulkNotifyForm] = useState({
    title: '',
    message: '',
    type: 'info',
  });
  const [bulkNotifyLoading, setBulkNotifyLoading] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    fetchUsersWithInstagram();
  }, []);

  const fetchUsersWithInstagram = async () => {
    setLoading(true);
    try {
      const res = await adminService.getUsers();
      setUsers(res.users || []);
      setError(null);
    } catch (err) {
      toast.error('Failed to fetch users');
      setUsers([]);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  // Filtering logic
  const filteredUsers = users.filter(user => {
    const matchesSearch =
      (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesInsta =
      instaFilter === 'all' ||
      (instaFilter === 'connected' && user.instagramConnected) ||
      (instaFilter === 'not_connected' && !user.instagramConnected);
    return matchesSearch && matchesRole && matchesInsta;
  });

  // Handle select all checkbox
  const handleSelectAll = (e) => {
    setSelectAll(e.target.checked);
    if (e.target.checked) {
      setSelectedUserIds(filteredUsers.map(u => u.id));
    } else {
      setSelectedUserIds([]);
    }
  };

  // Handle individual checkbox
  const handleSelectUser = (userId) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Bulk notification handler (use selectedUserIds if any, else filteredUsers)
  const handleBulkNotify = async (e) => {
    e.preventDefault();
    setBulkNotifyLoading(true);
    try {
      const userIds = selectedUserIds.length > 0 ? selectedUserIds : filteredUsers.map(u => u.id);
      if (userIds.length === 0) {
        toast.error('No users to notify');
        setBulkNotifyLoading(false);
        return;
      }
      await adminService.bulkNotifyUsers(
        userIds,
        bulkNotifyForm.message,
        bulkNotifyForm.type
      );
      toast.success('Bulk notification sent!');
      setShowBulkNotifyModal(false);
      setBulkNotifyForm({ title: '', message: '', type: 'info' });
      setSelectedUserIds([]);
      setSelectAll(false);
    } catch (err) {
      toast.error('Failed to send bulk notification');
    } finally {
      setBulkNotifyLoading(false);
    }
  };

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <FaInstagram className="text-pink-500 mr-2" size={28} />
            User Instagram Details
          </h1>
          <p className="text-gray-600">
            {error ? 'Failed to load users' : 'View users and their Instagram connection details'}
          </p>
        </div>
        <button
          className="btn-primary flex items-center gap-2"
          onClick={() => setShowBulkNotifyModal(true)}
          disabled={filteredUsers.length === 0}
        >
          <Send className="h-4 w-4" />
          Bulk Notify {selectedUserIds.length > 0 ? `(${selectedUserIds.length} selected)` : '(Filtered)'}
        </button>
      </div>

      {/* Filters */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div>
            <select
              className="input-field"
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value)}
            >
              <option value="all">All Roles</option>
              <option value="Brand">Brand</option>
              <option value="Influencer">Influencer</option>
            </select>
          </div>
          <div>
            <select
              className="input-field"
              value={instaFilter}
              onChange={e => setInstaFilter(e.target.value)}
            >
              <option value="all">All Instagram</option>
              <option value="connected">Connected</option>
              <option value="not_connected">Not Connected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="card p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="card p-6 mb-6">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={fetchUsersWithInstagram} className="btn-primary">
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Users Table */}
      {!loading && !error && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      aria-label="Select all users"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Instagram</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Followers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Engagement Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Average Likes</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Average Reach</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Overall Reach</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Media Count</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profile Picture</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="px-6 py-4 text-center text-gray-500">
                      No users found.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => handleSelectUser(user.id)}
                          aria-label={`Select user ${user.name}`}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-700">
                              {user.name?.charAt(0)}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.role}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.instagramConnected
                          ? <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">Connected</span>
                          : <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">Not Connected</span>
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.instagram?.followersCount ?? '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.instagram?.username ?? '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.instagram?.engagementRate ?? '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.instagram?.averageLikes ?? '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.instagram?.averageReach ?? '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.instagram?.overallReach ?? '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.instagram?.mediaCount ?? '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.instagram?.profilePicture
                          ? <img src={user.instagram.profilePicture} alt="Profile" className="h-8 w-8 rounded-full" />
                          : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bulk Notify Modal */}
      {showBulkNotifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 relative">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
              onClick={() => setShowBulkNotifyModal(false)}
            >
              &times;
            </button>
            <h2 className="text-xl font-bold mb-4">Send Bulk Notification</h2>
            <form onSubmit={handleBulkNotify} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Title</label>
                <input
                  type="text"
                  className="input-field w-full"
                  value={bulkNotifyForm.title}
                  onChange={e => setBulkNotifyForm({ ...bulkNotifyForm, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Message</label>
                <textarea
                  className="input-field w-full"
                  value={bulkNotifyForm.message}
                  onChange={e => setBulkNotifyForm({ ...bulkNotifyForm, message: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Type</label>
                <select
                  className="input-field w-full"
                  value={bulkNotifyForm.type}
                  onChange={e => setBulkNotifyForm({ ...bulkNotifyForm, type: e.target.value })}
                >
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="success">Success</option>
                  <option value="error">Error</option>
                </select>
              </div>
              <button type="submit" className="btn-primary w-full" disabled={bulkNotifyLoading}>
                {bulkNotifyLoading
                  ? 'Sending...'
                  : selectedUserIds.length > 0
                    ? `Send Notification to ${selectedUserIds.length} Selected`
                    : 'Send Notification to All Filtered'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default UserInstagramDetails;